import React, { useRef, useState } from "react";
import cn from "classnames";
import styles from "./styles.module.scss";

export const MIN_COLUMN_WIDTH = 64;
export const MAX_COLUMN_WIDTH = 560;

type Guide = { left: number; top: number; height: number };

type ColumnResizerProps = {
  /** The new width, reported once on release; null means reset to the default. */
  onResize: (width: number | null) => void;
  /** The table area, whose height the guide takes. */
  boundsRef: React.RefObject<HTMLElement | null>;
  label: string;
};

/**
 * The handle on the right edge of a header cell, as in the Apple Ads tables
 * of the client: while it is dragged a guide follows the pointer, and the
 * width is applied on release.
 */
const ColumnResizer: React.FC<ColumnResizerProps> = ({
  onResize,
  boundsRef,
  label,
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [guide, setGuide] = useState<Guide | null>(null);
  const next = useRef(0);

  const handlePointerDown = (event: React.PointerEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const cell = ref.current?.parentElement;
    const bounds = boundsRef.current;
    if (!cell || !bounds) return;

    // Measured from what is drawn now rather than from the given width.
    const cellBox = cell.getBoundingClientRect();
    const width = Math.round(cellBox.width);
    const cellLeft = cellBox.left;
    const box = bounds.getBoundingClientRect();
    const startX = event.clientX;
    next.current = width;

    const place = (proposed: number) =>
      setGuide({ left: cellLeft + proposed, top: box.top, height: box.height });
    place(width);

    const handleMove = (moveEvent: PointerEvent) => {
      // Whole pixels: under zoom the pointer moves in fractions.
      const proposed = Math.min(
        MAX_COLUMN_WIDTH,
        Math.max(
          MIN_COLUMN_WIDTH,
          Math.round(width + moveEvent.clientX - startX),
        ),
      );
      next.current = proposed;
      place(proposed);
    };
    const handleUp = () => {
      setGuide(null);
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      if (next.current !== width) onResize(next.current);
    };

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
  };

  return (
    <span
      ref={ref}
      className={cn(styles.resizer, { [styles.resizerActive]: guide !== null })}
      role="separator"
      aria-orientation="vertical"
      aria-label={`Resize ${label}`}
      title="Drag to resize, double-click to reset"
      onPointerDown={handlePointerDown}
      onClick={(event) => event.stopPropagation()}
      onDoubleClick={(event) => {
        event.stopPropagation();
        onResize(null);
      }}
      // The header is draggable for reordering, so a drag must not start
      // from the handle.
      draggable
      onDragStart={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      {guide && (
        <span
          className={styles.guide}
          style={{ left: guide.left, top: guide.top, height: guide.height }}
        />
      )}
    </span>
  );
};

export default ColumnResizer;
